import React, { useEffect, useState } from "react";
import { Button, Col, Input, Row, message, Switch, Spin, Modal } from "antd";
import FormLayout, { FormSection, FormItem } from "Components/form";

import TenantTypeForm from "./components/tenant-type";
import HousingTenantForm from "./components/housing-tenant";
import KinderGartenStudentForm from "./components/kindergarten-student";
import TitleNameSurnameForm from "./components/title-name-surname";
import ParentForm from "./components/parent";
import JobForm from "./components/job";
import PartnerTaskForm from "./components/partner-task";

import { Controller, useForm } from "react-hook-form";
import Yup from "ValidationModels/localizedYup";
import { TENANT_TYPES } from "Constants/";
import { RULES } from "Constants/rules";
import { fixDates, clean } from "Utils/";
import moment from "moment";
import {
  PartnerStoreModel,
  ACTION_TYPES,
} from "store/models/master-data/partner";
import { createComponentStore } from "easy-peasy";
import { yupResolver } from "@hookform/resolvers";
import FetchSelect from "Components/fetch-select";
import { ParameterStoreModel } from "store/models/parameters/general-parameter";
import { DISPLAY_MODE } from "Constants";

const usePartnerStore = createComponentStore(PartnerStoreModel);
const usePartnerTypesStore = createComponentStore(
  ParameterStoreModel("partnertypes")
);

const FormValidationSchema = Yup.object().shape({
  partnerRoles: Yup.array().required(),
  address: Yup.string().required(),
  name: Yup.string().required(),

  identityNumber: Yup.number().required(),
  tenantType: Yup.object().when("partnerRoles", {
    is: (roles) =>
      roles?.length === 1 && roles[0]?.rule?.code === RULES.RULE0017,
    then: Yup.object({
      id: Yup.number().required(),
    }),
  }),
  surname: Yup.string().when(["tenantType", "partnerRoles"], {
    is: (tentanttype, partnerRoles) =>
      tentanttype?.id === TENANT_TYPES["Kurum"] ||
      partnerRoles.find((role) => role.rule?.code === RULES.RULE0021),
    then: Yup.string(),
    otherwise: Yup.string().required(),
  }),
});

export default function PartnerForm({
  partner,
  onAfterSuccess,
  loading = false,
  mode = DISPLAY_MODE.READ_ONLY,
  ...props
}) {
  const [store, actions] = usePartnerStore();
  const [partnerTypesStore, partnerTypeActions] = usePartnerTypesStore();
  const [disabledPartnerRoles, setDisabledPartnerRoles] = useState([]);
  const [selectedTenantType, setSelectedTenantType] = useState();
  const [contractBlockade, setContractBlockade] = useState(false);
  const [childAge, setChildAge] = useState();

  const form = useForm({
    mode: "onChange",
    resolver: yupResolver(FormValidationSchema),
    defaultValues: partner,
  });

  const { control, handleSubmit, errors, formState, reset, watch } = form;

  const roles = watch("partnerRoles");

  useEffect(() => {
    partnerTypeActions.fetch();
  }, []);

  const partnerTypeList = partnerTypesStore.data;

  useEffect(() => {
    if (partner) {
      let partnerFormData = { ...partner };
      fixDates(partnerFormData);

      rolesDataToForm(partnerFormData);

      setChildAge(
        Math.floor(
          moment(new Date()).diff(partnerFormData.dateOfBirth, "months", true)
        )
      );

      setContractBlockade(partnerFormData.contractBlockade);
      reset(partnerFormData);
    }
  }, [partner]);

  useEffect(() => {
    if (roles && roles.length > 0) {
      let disabledOpts = [];
      if (roles[0]?.rule?.code === RULES.RULE0003) {
        partnerTypeList.forEach((type) => {
          if (type?.rule?.code !== RULES.RULE0003) disabledOpts.push(type);
        });
      } else {
        partnerTypeList.forEach((type) => {
          if (type?.rule?.code === RULES.RULE0003) disabledOpts.push(type);
        });
      }

      if (roles[0]?.rule?.code === RULES.RULE0021) {
        partnerTypeList.forEach((type) => {
          if (type?.rule?.code !== RULES.RULE0021) disabledOpts.push(type);
        });
      } else {
        partnerTypeList.forEach((type) => {
          if (type?.rule?.code === RULES.RULE0021) disabledOpts.push(type);
        });
      }

      setDisabledPartnerRoles(disabledOpts);
    } else {
      setDisabledPartnerRoles([]);
    }

    if (roles && roles.length !== 1) {
      setSelectedTenantType(null);
    }
  }, [roles]);

  useEffect(() => {
    if (store.response)
      switch (store.process) {
        case ACTION_TYPES.CREATED_DATA:
          message.success("Muhatap oluşturuldu.");
          if (!onAfterSuccess) return;
          onAfterSuccess(store.response);
          break;
        case ACTION_TYPES.UPDATED_DATA:
          message.success("Muhatap güncellendi.");
          if (!onAfterSuccess) return;
          onAfterSuccess(store.response);
          break;
        default:
          break;
      }
  }, [store.process, store.response]);

  const isInstitution =
    selectedTenantType === TENANT_TYPES["Kurum"] ||
    roles?.find((role) => role.rule?.code === RULES.RULE0021);

  const rolesDataToForm = (partnerFormData) => {
    let fixedPartnerRoles = [];

    partnerFormData.partnerRoles.map((role) => {
      fixedPartnerRoles.push(role.type);
    });

    partnerFormData.partnerRoles = fixedPartnerRoles;

    if (
      fixedPartnerRoles.length === 1 &&
      partnerFormData.partnerRoles[0]?.rule?.code === RULES.RULE0017
    ) {
      if (partnerFormData.hasOwnProperty("title")) {
        setSelectedTenantType(TENANT_TYPES["Kişi"]);
        partnerFormData.tenantType = { id: TENANT_TYPES["Kişi"] };
      } else {
        setSelectedTenantType(TENANT_TYPES["Kurum"]);
        partnerFormData.tenantType = { id: TENANT_TYPES["Kurum"] };
      }
    }
  };

  const relationsDataToForm = (partnerFormData) => {
    if (partnerFormData.hasOwnProperty("relations")) {
      partnerFormData.relations.forEach((relation) => {
        relation.relationId = { ...relation };
      });
    }
  };

  const formOnSubmit = async (form) => {
    clean(form);

    delete form.tenantType;

    form.partnerRoles = form.partnerRoles.map((item) => {
      return {
        type: {
          version: item.version,
          id: item.id,
        },
        version: 0,
      };
    });

    if (form.hasOwnProperty("title")) {
      form.title.version = 0;
    }

    if (mode === DISPLAY_MODE.EDIT) {
      form.partnerRoles.map((role) => {
        partner.partnerRoles.forEach((oldRole) => {
          if (role.type.id === oldRole.type.id) {
            role.id = oldRole.id;
            role.version = oldRole.version;
          }
        });
      });

      let updateData = { ...partner, ...form };
      if (!form.hasOwnProperty("relations")) {
        delete updateData.relations;
      }

      await actions.update(updateData);
    } else {
      await actions.create(form);
    }
  };

  const tenantTypeChangeHandler = (value, key) => {
    setSelectedTenantType(value);
  };

  const getConditionalForm = (rules, conditionalForm, exclude = false) => {
    //exclude true ise gelen rules dışındakilere formu gösterir
    if (Array.isArray(roles))
      return roles.find((selectedRole) =>
        rules.includes(selectedRole?.rule?.code)
      )
        ? exclude
          ? null
          : conditionalForm()
        : exclude
        ? conditionalForm()
        : null;
  };

  const getTitleNameSurname = () => (
    <TitleNameSurnameForm
      form={form}
      selectedTenantType={selectedTenantType}
      roles={roles}
      readOnly={mode === DISPLAY_MODE.READ_ONLY}
      isInstitution={isInstitution}
    />
  );

  const getTenantTypeSelect = () => {
    if (roles.length !== 1) return null;
    return (
      <TenantTypeForm
        form={form}
        onChange={tenantTypeChangeHandler}
        readOnly={mode === DISPLAY_MODE.READ_ONLY}
      />
    );
  };

  const getHousingTenant = () => (
    <HousingTenantForm form={form} readOnly={mode === DISPLAY_MODE.READ_ONLY} />
  );

  const getJobForm = () => (
    <JobForm form={form} readOnly={mode === DISPLAY_MODE.READ_ONLY} />
  );

  const getPartnerTaskForm = () => (
    <PartnerTaskForm form={form} readOnly={mode === DISPLAY_MODE.READ_ONLY} />
  );

  const getParentForm = () => (
    <ParentForm form={form} readOnly={mode === DISPLAY_MODE.READ_ONLY} />
  );

  const getKinderGartenStudentForm = () => (
    <KinderGartenStudentForm
      form={form}
      childAge={childAge}
      setChildAge={setChildAge}
      readOnly={mode === DISPLAY_MODE.READ_ONLY}
    />
  );

  const getFaxForm = () => (
    <Col xs={24} md={12}>
      <FormItem htmlFor="fax" label="Fax">
        <Controller
          control={control}
          name="fax"
          as={
            <Input
              readOnly={mode === DISPLAY_MODE.READ_ONLY}
              placeholder="Fax no giriniz"
            />
          }
        />
      </FormItem>
    </Col>
  );

  return (
    <Spin spinning={loading}>
      <form onSubmit={handleSubmit(formOnSubmit)} style={{ margin: "0 24px" }}>
        <FormLayout>
          <Col lg={24}>
            <FormSection title="Genel">
              <Row gutter={16}>
                <Col xs={24} lg={12}>
                  <FormItem
                    htmlFor="partnerRoles"
                    error={errors.partnerRoles}
                    label="Muhatap Tipi"
                  >
                    <FetchSelect
                      type="multiple"
                      api="/masterdata/partnertypes"
                      disabledOptions={disabledPartnerRoles}
                      form={form}
                      field="partnerRoles"
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      placeholder="Muhatap Tipi Seçiniz"
                    />
                  </FormItem>
                </Col>

                {getConditionalForm([RULES.RULE0017], getTenantTypeSelect)}
              </Row>

              {getConditionalForm(
                [
                  RULES.RULE0004,
                  RULES.RULE0002,
                  RULES.RULE0017,
                  RULES.RULE0018,
                  RULES.RULE0021,
                ],
                getTitleNameSurname
              )}

              <Row gutter={16}>
                {getConditionalForm(
                  [RULES.RULE0004, RULES.RULE0002, RULES.RULE0018],
                  getJobForm
                )}

                {getConditionalForm([RULES.RULE0004], getParentForm)}
              </Row>

              <Row gutter={16}>
                {getConditionalForm(
                  [
                    RULES.RULE0002,
                    RULES.RULE0004,
                    RULES.RULE0017,
                    RULES.RULE0018,
                  ],
                  getPartnerTaskForm
                )}

                {getConditionalForm(
                  [RULES.RULE0002, RULES.RULE0018],
                  getHousingTenant
                )}
              </Row>

              {getConditionalForm([RULES.RULE0003], getKinderGartenStudentForm)}
            </FormSection>
            <FormSection title="İletişim">
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <FormItem
                    htmlFor="email"
                    error={errors.email}
                    label="E-Posta"
                  >
                    <Controller
                      control={control}
                      name="email"
                      as={
                        <Input
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                          placeholder="E-Posta giriniz"
                        />
                      }
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    htmlFor="phone"
                    error={errors.phone}
                    label="Telefon"
                  >
                    <Controller
                      control={control}
                      name="phone"
                      as={
                        <Input
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                          placeholder="Telefon giriniz"
                        />
                      }
                    />
                  </FormItem>
                </Col>

                {getConditionalForm([RULES.RULE0003], getFaxForm, true)}
              </Row>
            </FormSection>
            <FormSection title="Konum">
              <Row gutter={16}>
                <Col lg={24}>
                  <FormItem
                    label="Adres"
                    error={errors.address}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="address"
                      as={
                        <Input.TextArea
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                          rows={3}
                          placeholder="Adres yazınız"
                        />
                      }
                    />
                  </FormItem>
                </Col>
              </Row>
            </FormSection>

            <FormSection title="Diğer Bilgiler">
              <Row gutter={16}>
                <Col lg={12}>
                  <FormItem
                    htmlFor="identityNumber"
                    required={true}
                    error={errors.identityNumber}
                    label={
                      isInstitution
                        ? "Vergi numarası giriniz"
                        : "Kimlik numarası"
                    }
                  >
                    <Controller
                      control={control}
                      name="identityNumber"
                      as={
                        <Input
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                          placeholder={
                            selectedTenantType === TENANT_TYPES["Kurum"]
                              ? "Vergi no giriniz"
                              : "Kimlik no giriniz"
                          }
                        />
                      }
                    />
                  </FormItem>
                </Col>

                <Col lg={12}>
                  <FormItem
                    htmlFor="taxOffice"
                    error={errors.taxOffice}
                    label="Vergi Dairesi"
                  >
                    <Controller
                      control={control}
                      name="taxOffice"
                      as={
                        <Input
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                          placeholder="Vergi dairesini giriniz"
                        />
                      }
                    />
                  </FormItem>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col lg={12}>
                  <FormItem htmlFor="contractBlockade" label="Sözleşme Blokajı">
                    <Controller
                      valueName="checked"
                      defaultValue={contractBlockade}
                      onClick={(c, e) => {
                        setContractBlockade(c);
                      }}
                      control={control}
                      checked={contractBlockade}
                      name="contractBlockade"
                      as={<Switch disabled={mode === DISPLAY_MODE.READ_ONLY} />}
                    />
                  </FormItem>
                </Col>
              </Row>
            </FormSection>
            {mode === DISPLAY_MODE.READ_ONLY ? null : (
              <Row
                justify="space-between"
                style={{
                  borderTop: "1px dashed rgb(166, 157, 152)",
                  position: "sticky",
                  bottom: 0,
                  padding: "16px 0",
                  backgroundColor: "#f2f2f2",
                }}
              >
                <Col>
                  <Button
                    loading={
                      store.process === ACTION_TYPES.CREATING_DATA ||
                      store.process === ACTION_TYPES.UPDATING_DATA
                    }
                    style={{ width: 120, marginRight: 8 }}
                    type="primary"
                    htmlType="submit"
                  >
                    Kaydet
                  </Button>

                  <Button
                    onClick={() => {
                      Modal.confirm({
                        title: "İptal",
                        content:
                          "Kaydetmediğiniz tüm değişiklikler, geri alınacaktır.",
                        okText: "Evet",
                        cancelText: "İptal",
                        onOk: () => {
                          reset();
                        },
                      });
                    }}
                  >
                    İptal
                  </Button>
                </Col>
              </Row>
            )}
          </Col>
        </FormLayout>
      </form>
    </Spin>
  );
}
